import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  Chip,
  Divider,
  Grid,
  Typography,
  Tooltip,
  Paper,
  Button,
  Popover,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WorkIcon from "@mui/icons-material/Work";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import { MdOutlineMail, MdOutlineLocalPhone, MdVerified } from "react-icons/md";
import { AiOutlineLinkedin } from "react-icons/ai";
import { SiWhatsapp } from "react-icons/si";
import { RxCrossCircled } from "react-icons/rx";

import Slider from "react-slick";
import VideoModal from "./VideoModal";
import LinearProgress from "./LinearProgress";

import { useSelector, useDispatch } from "react-redux";
import { CandidateProfile } from "../../../Redux/action/CandidateProfileAction";
import { VendorProfile } from "../../../Redux/action/VendorProfileAction";
import { ProfileProgressBarAction } from "../../../Redux/action/ProfileProgressBarAction";

import dummyDp from "../../../images/dummy_dp.png";
import videoThumb from "../../../images/video_img.jpg";
import Loader from "../../Common/Loader/loader";
import { toast } from "react-toastify";
import "./candidateDash.scss";
import { useNavigate } from "react-router-dom";
import {
  AssignmentOutlined,
  BuildOutlined,
  BusinessOutlined,
  Close,
  LocationOn,
  SchoolOutlined,
  Verified,
  VerifiedOutlined,
  VisibilityOutlined,
  WorkHistory,
  WorkspacePremiumOutlined,
} from "@mui/icons-material";

// ---------------------- IMAGE CAROUSEL ------------------------
const ProjectCarousel = ({ images }) => {
  if (!images?.length) return null;

  const settings = {
    dots: true,
    infinite: true,
    slidesToShow: images.length > 1 ? 2 : 1,
    speed: 500,
  };

  return (
    <Slider {...settings}>
      {images.map((img, i) => (
        <Paper
          key={i}
          elevation={0}
          sx={{
            p: 1,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <img
            src={img}
            alt=""
            style={{
              height: 180,
              width: "90%",
              borderRadius: 8,
              objectFit: "cover",
            }}
          />
        </Paper>
      ))}
    </Slider>
  );
};

// ------------------- MAIN COMPONENT ------------------
const CandidateDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userResult } = useSelector((state) => state.Auth);
  const vendorData = useSelector((state) => state?.VendorProfile?.result);
  const [openResume, setOpenResume] = React.useState(false);
  const loading = useSelector((state) => state.Profile.loading);
  const userInfo = useSelector((state) => state.Profile.result);
  const profileProgress = useSelector((state) => state.ProgressBAR.result);
  const [anchorElPrice, setAnchorElPrice] = useState(null);
  const id = userResult?.userid;
  const candidateVendorId = userInfo?.vendorId ?? null;

  const [showVideoModal, setShowVideoModal] = useState(false);
  const [isPricingOpen, setIsPricingOpen] = useState(false);

  useEffect(() => {
    dispatch(CandidateProfile(id));
    dispatch(ProfileProgressBarAction(id));
  }, [id]);

  useEffect(() => {
    if (candidateVendorId) dispatch(VendorProfile(candidateVendorId));
  }, [candidateVendorId]);

  const handleCopyLinkedIn = () => {
    if (!userInfo?.linkedIn) return toast.error("No LinkedIn profile found");

    navigator.clipboard.writeText(userInfo.linkedIn);
    toast.success("LinkedIn copied");
  };

  const handlePriceClick = (e) => {
    setAnchorElPrice(anchorElPrice ? null : e.currentTarget);
    setIsPricingOpen((v) => !v);
  };
  const openPrice = Boolean(anchorElPrice);

  const truncate = (str, maxLength) =>
    str ? (str.length > maxLength ? str.slice(0, maxLength) + "..." : str) : "";

  if (loading) {
    return (
      <Box
        sx={{
          height: "60vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Loader />
      </Box>
    );
  }

  const statusBadge = userInfo?.emailVerified
    ? {
        label: "Verified",
        icon: <MdVerified color="#2b9348" />,
        sx: { background: "#d1fadf", color: "#1e7a40" },
      }
    : {
        label: "Unverified",
        icon: <RxCrossCircled color="red" />,
        sx: { background: "#ffe1e1", color: "#b91c1c" },
      };

  return (
    <>
      <Box sx={{ px: 3, py: 4 }}>
        <Grid container spacing={3}>
          {/* ---------- LEFT PROFILE SIDEBAR ---------- */}
          <Grid item xs={12} md={4} lg={3}>
            <Box
              sx={{
                position: { xs: "static", md: "sticky" },
                top: 20,
                zIndex: 5,
              }}
            >
              <Card
                sx={{
                  p: { xs: 2, md: 3 },
                  borderRadius: 4,
                  textAlign: "center",
                  background: "linear-gradient(135deg, #EBF5FF, #FFFFFF)",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
                }}
              >
                <Avatar
                  src={userInfo?.profileImageUrl || dummyDp}
                  alt="User profile picture"
                  imgProps={{
                    loading: "eager",
                    fetchpriority: "high",
                    decoding: "async",
                  }}
                  sx={{
                    width: 200,
                    height: 200,
                    mx: "auto",
                    mb: 2,
                    border: "4px solid #fff",
                    boxShadow: "0px 4px 15px rgba(0,0,0,0.1)",
                  }}
                />

                <Typography variant="h6" fontWeight={700}>
                  {truncate(
                    userInfo?.firstName,
                    window.innerWidth < 520 ? 6 : 10
                  )}{" "}
                  {truncate(
                    userInfo?.lastName,
                    window.innerWidth < 520 ? 6 : 10
                  )}
                </Typography>

                <Chip
                  icon={statusBadge.icon}
                  label={statusBadge.label}
                  size="small"
                  sx={{ ...statusBadge.sx, mt: 1, fontWeight: 600 }}
                />

                <Divider sx={{ my: 3 }} />

                <Typography
                  fontWeight={700}
                  sx={{
                    textTransform: "uppercase",
                    textAlign: "left",
                    fontSize: "13px",
                    opacity: 0.9,
                    mb: 2,
                  }}
                >
                  Contact Information
                </Typography>

                <Box sx={{ display: "grid", gap: 2 }}>
                  {/* Email */}
                  <Tooltip arrow title={userInfo?.email}>
                    <Box
                      onClick={() => window.open(`mailto:${userInfo?.email}`)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        cursor: "pointer",
                        fontSize: "15px",
                        color: "#0E3F6F",
                        transition: "0.3s",
                        "&:hover": { color: "#286A94" },
                      }}
                    >
                      <MdOutlineMail size={20} color="#286a94" />
                      <Typography>
                        {userInfo?.email?.length > 30
                          ? userInfo?.email?.slice(0, 30) + "..."
                          : userInfo?.email}
                      </Typography>
                    </Box>
                  </Tooltip>

                  {/* Phone */}
                  {userInfo?.mobile && (
                    <Tooltip arrow title="Call">
                      <Box
                        onClick={() =>
                          window.open(
                            `tel:+${userInfo?.dialCode}${userInfo?.mobile}`
                          )
                        }
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          fontSize: "15px",
                          color: "#0E3F6F",
                          transition: "0.3s",
                          "&:hover": { color: "#286A94" },
                          gap: 1,
                        }}
                      >
                        <MdOutlineLocalPhone size={20} color="#286a94" />
                        <Typography>
                          +{userInfo?.dialCode} {userInfo?.mobile}
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}

                  {/* LinkedIn */}
                  {userInfo?.linkedIn && (
                    <Tooltip arrow title="Click to Copy URL">
                      <Box
                        onClick={handleCopyLinkedIn}
                        sx={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          fontSize: "15px",
                          color: "#0E3F6F",
                          transition: "0.3s",
                          "&:hover": { color: "#286A94" },
                          gap: 1,
                        }}
                      >
                        <AiOutlineLinkedin size={20} color="#286a94" />
                        <Typography>LinkedIn Profile</Typography>
                      </Box>
                    </Tooltip>
                  )}

                  {/* WhatsApp */}
                  {userInfo?.whatsapp && (
                    <Tooltip arrow title="Open WhatsApp Chat">
                      <Box
                        onClick={() =>
                          window.open(
                            `https://wa.me/${userInfo?.dialCode}${userInfo?.whatsapp}`,
                            "_blank"
                          )
                        }
                        sx={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          fontSize: "15px",
                          color: "#0E3F6F",
                          transition: "0.3s",
                          "&:hover": { color: "#286A94" },
                          gap: 1,
                        }}
                      >
                        <SiWhatsapp size={20} color="#25D366" />
                        <Typography>
                          +{userInfo?.dialCode} {userInfo?.whatsapp}
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              </Card>
            </Box>
          </Grid>

          {/* ------------ RIGHT SIDE CONTENT ------------- */}
          <Grid item xs={12} md={8} lg={9}>
            <Card
              sx={{
                p: 4,
                borderRadius: 4,
                boxShadow: "0px 8px 24px rgba(0,0,0,0.04)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  gap: 2,
                  pb: 1,
                }}
              >
                {/* LEFT SIDE */}
                {/* <Box sx={{ flex: 1, minWidth: "250px" }}>
                  <Typography variant="h5" fontWeight={700}>
                    {userInfo?.currentDesignation || "NA"}
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.5,
                      color: "#6b7280",
                      textTransform: "uppercase",
                    }}
                  >
                    Candidate ID:{" "}
                    <strong style={{ color: "#0E3F6F" }}>
                      {userInfo?.candidateEmpId || "--"}
                    </strong>
                  </Typography>

                  <Box sx={{ mt: 2, maxWidth: 320 }}>
                    <LinearProgress profileProgress={profileProgress || 0} />
                  </Box>
                </Box> */}
                <Box sx={{ flex: 1, minWidth: "250px" }}>
                  <Box>
                    {/* Designation + Verified */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          color: "#0f172a",
                          lineHeight: 1.3,
                        }}
                      >
                        {userInfo?.currentDesignation || "N/A"}
                      </Typography>

                      <Tooltip
                        title={
                          userInfo?.emailVerified
                            ? "Verified profile"
                            : "Profile not verified"
                        }
                      >
                        {userInfo?.emailVerified ? (
                          <Verified
                            sx={{
                              color: "#2b9348",
                              fontSize: 22,
                            }}
                          />
                        ) : (
                          <VerifiedOutlined
                            sx={{
                              color: "#b91c1c",
                              fontSize: 22,
                            }}
                          />
                        )}
                      </Tooltip>
                    </Box>

                    {/* Candidate ID */}
                    <Box
                      sx={{
                        mt: 0.8,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.8,
                        px: 1.5,
                        py: 0.4,
                        borderRadius: 999,
                        background: "#F1F7FB",
                        border: "1px solid #E2E8F0",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 14,
                          color: "#64748b",
                          fontWeight: 500,
                          textTransform: "uppercase",
                        }}
                      >
                        Candidate ID :
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: "#286a94",
                          letterSpacing: "0.3px",
                        }}
                      >
                        {userInfo?.candidateEmpId || "--"}
                      </Typography>
                    </Box>
                  </Box>

                  {
                    <Box sx={{ mt: 2, maxWidth: 320 }}>
                      <LinearProgress profileProgress={profileProgress || 0} />
                    </Box>
                  }
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  {/* PRICE BUTTON */}
                  {!userInfo?.vendorId &&
                    userInfo?.candidatePrice &&
                    parseInt(userInfo?.keyExperience || 0) >= 1 && (
                      <>
                        <Button
                          variant="outlined"
                          onClick={handlePriceClick}
                          startIcon={
                            <PaidOutlinedIcon sx={{ color: "#215b7f" }} />
                          }
                          endIcon={
                            isPricingOpen ? (
                              <FaChevronDown size={14} />
                            ) : (
                              <FaChevronRight size={14} />
                            )
                          }
                          sx={{
                            textTransform: "none",
                            borderColor: "#215b7f",
                            color: "#215b7f",
                            fontWeight: 600,
                            px: 2.2,
                            "&:hover": {
                              backgroundColor: "rgba(33, 91, 127, 0.1)",
                              borderColor: "#215b7f",
                            },
                          }}
                        >
                          Price
                        </Button>

                        <Popover
                          open={openPrice}
                          anchorEl={anchorElPrice}
                          onClose={() => {
                            setAnchorElPrice(null);
                            setIsPricingOpen(false);
                          }}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                          }}
                        >
                          <Paper
                            sx={{
                              p: 1.5,
                              minWidth: 230,
                              borderRadius: 2,
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            {(() => {
                              const currency =
                                userInfo?.candidatePrice?.currency;
                              const currencySymbol =
                                currency === "INR"
                                  ? "₹"
                                  : currency === "USD"
                                  ? "$"
                                  : currency + " ";

                              return (
                                <>
                                  {/* Per Hour */}
                                  {userInfo?.candidatePrice?.perHour && (
                                    <>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: "#215b7f",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {currencySymbol}
                                        {userInfo?.candidatePrice?.perHour} / hr
                                      </Typography>
                                      <Divider
                                        sx={{
                                          my: 1,
                                          borderColor: "rgba(33,91,127,0.15)",
                                        }}
                                      />
                                    </>
                                  )}

                                  {/* Per Day */}
                                  {userInfo?.candidatePrice?.perDay && (
                                    <>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: "#215b7f",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {currencySymbol}
                                        {userInfo?.candidatePrice?.perDay} / day
                                      </Typography>
                                      <Divider
                                        sx={{
                                          my: 1,
                                          borderColor: "rgba(33,91,127,0.15)",
                                        }}
                                      />
                                    </>
                                  )}

                                  {/* Per Week */}
                                  {userInfo?.candidatePrice?.perWeek && (
                                    <>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: "#215b7f",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {currencySymbol}
                                        {userInfo?.candidatePrice?.perWeek} /
                                        week
                                      </Typography>
                                      <Divider
                                        sx={{
                                          my: 1,
                                          borderColor: "rgba(33,91,127,0.15)",
                                        }}
                                      />
                                    </>
                                  )}

                                  {/* Per Month */}
                                  {userInfo?.candidatePrice?.perMonth && (
                                    <>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: "#215b7f",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {currencySymbol}
                                        {userInfo?.candidatePrice?.perMonth} /
                                        month
                                      </Typography>
                                    </>
                                  )}
                                  {userInfo?.candidatePrice?.salaryMode ===
                                    "CTC" &&
                                    userInfo?.candidatePrice?.ctc && (
                                      <>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: "#215b7f",
                                            fontWeight: 600,
                                          }}
                                        >
                                          {currencySymbol}
                                          {userInfo?.candidatePrice?.ctc} / CTC
                                        </Typography>
                                      </>
                                    )}
                                </>
                              );
                            })()}
                          </Paper>
                        </Popover>
                      </>
                    )}

                  {/* RESUME */}
                  {userInfo?.resumeFileUrl && (
                    <Button
                      variant="outlined"
                      onClick={() => setOpenResume(true)}
                      startIcon={
                        <VisibilityOutlined sx={{ color: "#215b7f" }} />
                      }
                      sx={{
                        textTransform: "none",
                        borderColor: "#215b7f",
                        color: "#215b7f !important",
                        fontWeight: 600,
                        px: 2.5,
                        "&:hover": {
                          backgroundColor: "rgba(33, 91, 127, 0.08)",
                          borderColor: "#215b7f",
                        },
                      }}
                    >
                      Resume
                    </Button>
                  )}

                  {/* INTRODUCTION VIDEO */}
                  {userInfo?.videoUrl && (
                    <Button
                      variant="outlined"
                      startIcon={
                        <PlayCircleOutlineIcon sx={{ color: "#215b7f" }} />
                      }
                      onClick={() => setShowVideoModal(true)}
                      sx={{
                        textTransform: "none",
                        borderColor: "#215b7f",
                        color: "#215b7f !important",
                        fontWeight: 600,
                        px: 2.5,
                        "&:hover": {
                          backgroundColor: "rgba(33, 91, 127, 0.08)",
                          borderColor: "#215b7f",
                        },
                      }}
                    >
                      Introduction
                    </Button>
                  )}
                </Box>
              </Box>

              {userInfo?.overview && (
                <Box
                  sx={{
                    mt: 0.5,
                    p: 2.5,
                    borderRadius: 3,
                    background: "#F8FAFC",
                    borderLeft: "4px solid #286a94",
                  }}
                >
                  {/* Label */}
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#286a94",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      mb: 0.75,
                    }}
                  >
                    Professional Summary
                  </Typography>

                  {/* Content */}
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: "#475569",
                      lineHeight: 1.7,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {userInfo.overview}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 4 }} />

              <Box sx={{ mt: 4 }}>
                <Grid container spacing={2}>
                  {/* Location */}
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        background: "#F1F7FB",
                        border: "1px solid #E2E8F0",
                        display: "flex",
                        gap: 1.5,
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: "50%",
                          background: "#E6F0F8",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <LocationOn sx={{ color: "#286a94" }} />
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#286a94",
                            textTransform: "uppercase",
                          }}
                        >
                          Location
                        </Typography>
                        <Typography sx={{ color: "#475569", fontSize: 14 }}>
                          {[
                            userInfo?.address,
                            userInfo?.city,
                            userInfo?.state,
                            userInfo?.country,
                          ]
                            .filter(Boolean)
                            .join(", ") || "—"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Experience */}
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        background: "#F1F7FB",
                        border: "1px solid #E2E8F0",
                        display: "flex",
                        gap: 1.5,
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: "50%",
                          background: "#E6F0F8",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <WorkHistory sx={{ color: "#286a94" }} />
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#286a94",
                            textTransform: "uppercase",
                          }}
                        >
                          Experience
                        </Typography>
                        <Typography sx={{ color: "#475569", fontSize: "15px" }}>
                          {(() => {
                            const yearsRaw = parseInt(
                              userInfo?.keyExperience,
                              10
                            );
                            const monthsRaw = parseInt(
                              userInfo?.keyExperienceInMonth,
                              10
                            );
                            const years = Number.isNaN(yearsRaw) ? 0 : yearsRaw;
                            const months = Number.isNaN(monthsRaw)
                              ? 0
                              : monthsRaw;

                            if (years === 0 && months === 0) {
                              return "Fresher";
                            }
                            if (years === 0) {
                              return `${months} Month${months > 1 ? "s" : ""}`;
                            }
                            if (months === 0) {
                              return `${years} Year${years > 1 ? "s" : ""}`;
                            }
                            return `${years} Year${
                              years > 1 ? "s" : ""
                            } ${months} Month${months > 1 ? "s" : ""}`;
                          })()}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {candidateVendorId && (
                <Box sx={{ mt: 4 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        background: "#E6F0F8",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <BusinessOutlined
                        sx={{
                          color: "#286a94",
                          fontSize: 22,
                        }}
                      />
                    </Box>

                    <Typography variant="h6" fontWeight={700} color="#286a94">
                      Vendor Information
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    {/* Vendor Name */}
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ textAlign: "left" }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "14px",
                            color: "#0E3F6F",
                            mb: 0.5,
                          }}
                        >
                          Vendor Name
                        </Typography>

                        <Typography sx={{ color: "#475569", fontSize: "15px" }}>
                          {vendorData?.firstName + " " + vendorData?.lastName ||
                            "N/A"}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Vendor ID */}
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ textAlign: "left" }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "14px",
                            color: "#0E3F6F",
                            mb: 0.5,
                          }}
                        >
                          Vendor ID
                        </Typography>

                        {vendorData?.vendorEmpId ? (
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              px: 1.5,
                              py: 0.4,
                              borderRadius: "999px",
                              backgroundColor: "#E6F0F8",
                              border: "1px solid #B6D4EA",
                              color: "#0E3F6F",
                              fontSize: "13px",
                              fontWeight: 600,
                              letterSpacing: "0.3px",
                            }}
                          >
                            {vendorData.vendorEmpId}
                          </Box>
                        ) : (
                          <Typography
                            sx={{ color: "#475569", fontSize: "15px" }}
                          >
                            N/A
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    {/* Company name with it's logo */}
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ textAlign: "left" }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "14px",
                            color: "#0E3F6F",
                            mb: 0.5,
                          }}
                        >
                          Company Name
                        </Typography>

                        {vendorData?.company ? (
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 1,
                              px: 1.5,
                              py: 0.5,
                              borderRadius: "999px",
                              backgroundColor: "#E6F0F8",
                              border: "1px solid #B6D4EA",
                              color: "#0E3F6F",
                              fontSize: "13px",
                              fontWeight: 600,
                              letterSpacing: "0.3px",
                            }}
                          >
                            {/* Company Logo */}
                            {vendorData?.companyLogoUrl && (
                              <Avatar
                                src={vendorData.companyLogoUrl}
                                alt={vendorData.company || "Company logo"}
                                imgProps={{
                                  loading: "eager",
                                  decoding: "async",
                                }}
                                sx={{
                                  width: 22,
                                  height: 22,
                                  bgcolor: "#ffffff",
                                  border: "1px solid #B6D4EA",
                                  flexShrink: 0,
                                }}
                              />
                            )}

                            {/* Company Name */}
                            <Typography
                              sx={{ fontSize: "13px", fontWeight: 600 }}
                            >
                              {vendorData.company}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography
                            sx={{ color: "#475569", fontSize: "15px" }}
                          >
                            N/A
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* Vendor Email */}
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ textAlign: "left" }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "14px",
                            color: "#0E3F6F",
                            mb: 0.5,
                          }}
                        >
                          Vendor Email
                        </Typography>

                        <Typography sx={{ color: "#475569", fontSize: "15px" }}>
                          {vendorData?.email || "N/A"}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Vendor Phone */}
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ textAlign: "left" }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "14px",
                            color: "#0E3F6F",
                            mb: 0.5,
                          }}
                        >
                          Contact Number
                        </Typography>

                        <Typography sx={{ color: "#475569", fontSize: "15px" }}>
                          {`+${vendorData?.dialCode} ${vendorData?.mobile}` ||
                            "N/A"}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Divider sx={{ my: 4 }} />
              {/* SKILLS */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "50%",
                      background: "#E6F0F8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <BuildOutlined
                      sx={{
                        color: "#286a94",
                        fontSize: 22,
                      }}
                    />
                  </Box>

                  <Box>
                    <Typography variant="h6" fontWeight={700} color="#286a94">
                      Skills
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                      Technical & professional expertise
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2, mt: 1 }}>
                {userInfo?.candidateSkills?.map((item, idx) => (
                  <Chip
                    key={idx}
                    label={item.skills}
                    sx={{
                      background: "#e9f2ff",
                      borderRadius: "999px",
                      px: 2,
                      py: 1,
                      fontWeight: 600,
                      fontSize: "13px",
                    }}
                  />
                ))}
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* ------- PROJECT ------- */}
              <Box sx={{ mt: 4 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "50%",
                      background: "#E6F0F8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AssignmentOutlined
                      sx={{
                        color: "#286a94",
                        fontSize: 22,
                      }}
                    />
                  </Box>

                  <Typography variant="h6" fontWeight={700} color="#286a94">
                    Projects
                  </Typography>
                </Box>
                {(!userInfo?.candidateProjects ||
                  userInfo?.candidateProjects.length === 0) && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 4,
                      textAlign: "center",
                      border: "2px dashed #bcd3df",
                      borderRadius: 3,
                      background: "#f8fcff",
                    }}
                  >
                    <Typography
                      sx={{ mb: 2, fontSize: "16px", color: "#4b5563" }}
                    >
                      You haven’t added any projects yet.
                    </Typography>

                    <Button
                      variant="contained"
                      onClick={() => navigate("/show-candidate-projects")}
                      sx={{
                        background: "#286a94",
                        fontWeight: 600,
                        px: 3,
                        py: 1.2,
                        borderRadius: "30px",
                        textTransform: "none",
                        fontSize: "15px",
                        "&:hover": {
                          background: "#1f5575",
                        },
                      }}
                    >
                      ➕ Add Project
                    </Button>
                  </Box>
                )}

                {/* Show Projects If Exists */}
                {userInfo?.candidateProjects?.length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ mt: 2 }}>
                      {userInfo.candidateProjects.map((proj, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            background: "#F9FAFB",
                            border: "1px solid #E5E7EB",
                            borderRadius: 3,
                            p: 3,
                            mb: 3,
                          }}
                        >
                          {/* Project Header */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 2,
                            }}
                          >
                            <Chip
                              label={`#${idx + 1}`}
                              sx={{
                                background: "#286a94",
                                color: "white",
                                mr: 2,
                                fontWeight: 600,
                              }}
                            />
                            <Typography
                              sx={{ fontSize: "18px", fontWeight: 600 }}
                            >
                              {proj.projectName || "Untitled Project"}
                            </Typography>
                          </Box>

                          {/* Project Details */}
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography
                                sx={{ fontWeight: 600, color: "#0E3F6F" }}
                              >
                                Role
                              </Typography>
                              <Typography sx={{ color: "#475569" }}>
                                {proj.role || "--"}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Typography
                                sx={{ fontWeight: 600, color: "#0E3F6F" }}
                              >
                                Duration
                              </Typography>
                              <Typography sx={{ color: "#475569" }}>
                                {proj.duration || "--"}
                              </Typography>
                            </Grid>

                            {/* 🔗 Clickable URL */}
                            <Grid item xs={12}>
                              <Typography
                                sx={{ fontWeight: 600, color: "#0E3F6F" }}
                              >
                                Project URL
                              </Typography>

                              {proj?.projectUrl ? (
                                <Typography
                                  component="a"
                                  href={
                                    proj.projectUrl.startsWith("http")
                                      ? proj.projectUrl
                                      : `https://${proj.projectUrl}`
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    fontSize: "15px",
                                    color: "#2563eb",
                                    wordBreak: "break-word",
                                    cursor: "pointer",
                                    "&:hover": { textDecoration: "underline" },
                                  }}
                                >
                                  {proj.projectUrl}
                                </Typography>
                              ) : (
                                <Typography sx={{ color: "#475569" }}>
                                  --
                                </Typography>
                              )}
                            </Grid>

                            {/* Description */}
                            <Grid item xs={12}>
                              <Typography
                                sx={{ fontWeight: 600, color: "#0E3F6F" }}
                              >
                                Description
                              </Typography>
                              <Typography
                                sx={{
                                  color: "#475569",
                                  whiteSpace: "pre-line",
                                }}
                              >
                                {proj.description || "--"}
                              </Typography>
                            </Grid>

                            {/* Technologies */}
                            <Grid item xs={12}>
                              <Typography fontWeight={600} color="#0E3F6F">
                                Technology Used
                              </Typography>

                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1,
                                  mt: 1,
                                }}
                              >
                                {proj?.technologies?.length > 0 ? (
                                  proj.technologies.map((tech, i) => (
                                    <Chip
                                      key={i}
                                      label={tech}
                                      sx={{
                                        background: "#eef6ff",
                                        fontWeight: 500,
                                      }}
                                    />
                                  ))
                                ) : (
                                  <Typography sx={{ color: "#475569" }}>
                                    --
                                  </Typography>
                                )}
                              </Box>
                            </Grid>
                          </Grid>

                          {/* Images */}
                          {proj?.projectImages?.length > 0 && (
                            <Box sx={{ mt: 3 }}>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  mb: 1,
                                  fontSize: "14px",
                                  color: "#0E3F6F",
                                }}
                              >
                                Screenshots
                              </Typography>

                              <ProjectCarousel images={proj.projectImages} />
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>

                    {/* Add Another Project Button */}
                    <Button
                      variant="outlined"
                      onClick={() => navigate("/show-candidate-projects")}
                      sx={{
                        borderColor: "#286a94",
                        color: "#286a94",
                        fontWeight: 600,
                        textTransform: "none",
                        mt: 2,
                        borderRadius: "30px",
                        px: 3,
                        "&:hover": {
                          background: "#286a94",
                          color: "white",
                        },
                      }}
                    >
                      ➕ Add More Projects
                    </Button>
                  </>
                )}
              </Box>
              <Divider sx={{ my: 3 }} />

              {/* ---------- EDUCATION ---------- */}
              <Box sx={{ mt: 5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      background: "#E6F0F8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SchoolOutlined
                      sx={{
                        color: "#286a94",
                        fontSize: 22,
                      }}
                    />
                  </Box>

                  <Typography variant="h6" fontWeight={700} color="#286a94">
                    Education
                  </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* EMPTY STATE WITH ADD BUTTON */}
                {(!userInfo?.educations ||
                  userInfo?.educations?.length === 0) && (
                  <Box
                    sx={{
                      p: 4,
                      textAlign: "center",
                      border: "2px dashed #bcd3df",
                      borderRadius: 3,
                      background: "#f8fcff",
                      color: "#475569",
                    }}
                  >
                    🎓 No education records found.
                    <Box sx={{ mt: 3 }}>
                      <button
                        onClick={() => navigate("/candidate-profile")}
                        style={{
                          background: "#286a94",
                          padding: "8px 12px",
                          borderRadius: "8px",
                          border: "none",
                          cursor: "pointer",
                          color: "white",
                          fontWeight: 600,
                          transition: "0.2s",
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.background = "#1d4c6b")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.background = "#286a94")
                        }
                      >
                        ➕ Add Education
                      </button>
                    </Box>
                  </Box>
                )}

                {/* LIST WHEN EDUCATION EXISTS */}
                {userInfo?.educations?.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    {userInfo.educations.map((item, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          p: 3,
                          borderRadius: 3,
                          mb: 2.5,
                          background: "#F9FAFB",
                          border: "1px solid #E5E7EB",
                          transition: "0.25s",
                          "&:hover": { background: "#f1f8ff" },
                        }}
                      >
                        {/* Header */}
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 2 }}
                        >
                          <Chip
                            label={`#${idx + 1}`}
                            sx={{
                              background: "#286a94",
                              color: "white",
                              mr: 2,
                              fontWeight: 600,
                              px: 1,
                            }}
                          />
                          <Typography
                            sx={{ fontSize: "18px", fontWeight: 600 }}
                          >
                            {item?.degreeName || item?.type || "Education"}
                          </Typography>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {/* Details */}
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "14px",
                                textTransform: "uppercase",
                                color: "#0E3F6F",
                              }}
                            >
                              University / Board
                            </Typography>
                            <Typography
                              sx={{ fontSize: "15px", color: "#475569" }}
                            >
                              {item?.institutionName ||
                                item?.board ||
                                item?.university ||
                                "--"}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={3}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "14px",
                                textTransform: "uppercase",
                                color: "#0E3F6F",
                              }}
                            >
                              Passing Year
                            </Typography>
                            <Typography
                              sx={{ fontSize: "15px", color: "#475569" }}
                            >
                              {item?.educationStatus === "PURSUING"
                                ? "Pursuing"
                                : item?.passingYear || "--"}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={3}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "14px",
                                textTransform: "uppercase",
                                color: "#0E3F6F",
                              }}
                            >
                              Grade / Percentage
                            </Typography>
                            <Typography
                              sx={{ fontSize: "15px", color: "#475569" }}
                            >
                              {item?.percentage ? `${item.percentage}%` : "--"}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
              <Divider sx={{ my: 3 }} />
              {/* ---------- CERTIFICATIONS ---------- */}
              <Box sx={{ mt: 5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      background: "#E6F0F8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <WorkspacePremiumOutlined
                      sx={{
                        color: "#286a94",
                        fontSize: 22,
                      }}
                    />
                  </Box>

                  <Typography variant="h6" fontWeight={700} color="#286a94">
                    Certifications
                  </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* EMPTY STATE */}
                {(!userInfo?.candidateCertificate ||
                  userInfo?.candidateCertificate.length === 0) && (
                  <Box
                    sx={{
                      p: 4,
                      textAlign: "center",
                      border: "2px dashed #bcd3df",
                      borderRadius: 3,
                      background: "#f8fcff",
                      color: "#475569",
                    }}
                  >
                    🎓 No certifications added yet.
                    <Box sx={{ mt: 3 }}>
                      <button
                        onClick={() => navigate("/candidate-profile")}
                        style={{
                          background: "#286a94",
                          padding: "8px 12px",
                          borderRadius: "8px",
                          border: "none",
                          cursor: "pointer",
                          color: "white",
                          fontWeight: 600,
                          transition: "0.2s",
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.background = "#1d4c6b")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.background = "#286a94")
                        }
                      >
                        ➕ Add Certificate
                      </button>
                    </Box>
                  </Box>
                )}

                {/* LIST OF CERTIFICATIONS */}
                {userInfo?.candidateCertificate?.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    {userInfo.candidateCertificate.map((item, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          background: "#F9FAFB",
                          border: "1px solid #E5E7EB",
                          p: 2.5,
                          borderRadius: 3,
                          mb: 2.5,
                        }}
                      >
                        {/* Header */}
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 2 }}
                        >
                          <Chip
                            label={`#${idx + 1}`}
                            sx={{
                              background: "#286a94",
                              color: "white",
                              mr: 2,
                              fontWeight: 600,
                            }}
                          />
                          <Typography
                            sx={{ fontSize: "18px", fontWeight: 600 }}
                          >
                            {item?.courseName || "Untitled Certification"}
                          </Typography>
                        </Box>

                        {/* Details */}
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "14px",
                                color: "#0E3F6F",
                              }}
                            >
                              Issued By
                            </Typography>
                            <Typography
                              sx={{ fontSize: "15px", color: "#475569" }}
                            >
                              {item?.courseProvider || "--"}
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "14px",
                                color: "#0E3F6F",
                              }}
                            >
                              Duration
                            </Typography>
                            <Typography
                              sx={{ fontSize: "15px", color: "#475569" }}
                            >
                              {item?.duration || "--"}
                            </Typography>
                          </Grid>

                          {/* URL Preview */}
                          {item?.certificateImageUrl && (
                            <Grid item xs={12}>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "14px",
                                  color: "#0E3F6F",
                                }}
                              >
                                Certificate
                              </Typography>

                              <Typography
                                component="a"
                                href={item?.certificateImageUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  fontSize: "15px",
                                  color: "#2563eb",
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 1,
                                  textDecoration: "none",
                                  "&:hover": { textDecoration: "underline" },
                                }}
                              >
                                📄 View Document
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Dialog
        open={openResume}
        onClose={() => setOpenResume(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 600,
          }}
        >
          Resume Preview
          <IconButton onClick={() => setOpenResume(false)}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          <iframe
            src={userInfo?.resumeFileUrl}
            title="Resume"
            width="100%"
            height="600px"
            style={{ border: "none" }}
          />
        </DialogContent>
      </Dialog>

      {/* VIDEO MODAL */}
      <VideoModal
        showModal={showVideoModal}
        handleClose={() => setShowVideoModal(false)}
        title="Introduction"
        body={
          userInfo?.videoUrl ? (
            <video width="100%" height="300px" controls>
              <source src={userInfo.videoUrl} type="video/mp4" />
            </video>
          ) : (
            <img src={videoThumb} alt="No Video" />
          )
        }
      />
    </>
  );
};

export default CandidateDashboard;
